import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { NavigationCancel, NavigationCancellationCode, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { AppService } from '@App';
import { AccountSettingsDTO } from '@DTOs';
import { Account, UWResponse } from '@Models';
import { IConfigService } from 'app/config/iconfigservice';
import { defer, map, Observable, shareReplay, Subscription } from 'rxjs';
import { BaseService } from '../base.service';

@Injectable({ providedIn: 'root' })
export class CaseSummarySettingsService extends BaseService implements OnDestroy {
    private caseId?: string;
    private settings = new Map<string, Observable<AccountSettingsDTO[]>>();
    private navigationSubscription?: Subscription;
    private navigationCompleted = false;

    constructor(
        appService: AppService,
        configService: IConfigService,
        httpClient: HttpClient,
        router: Router,
        @Optional() @SkipSelf() private parent: CaseSummarySettingsService | null = null
    ) {
        super(appService, configService, httpClient);
        if (parent) {
            this.caseId = parent.caseId;
            this.settings = new Map(parent.settings);
        } else {
            this.navigationSubscription = router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    this.navigationCompleted = true;
                } else if ((event instanceof NavigationStart && this.navigationCompleted) || event instanceof NavigationError
                    || (event instanceof NavigationCancel && event.code !== NavigationCancellationCode.Redirect)) {
                    this.navigationCompleted = false;
                    this.caseId = undefined;
                    this.settings.clear();
                }
            });
        }
    }

    ngOnDestroy() {
        this.navigationSubscription?.unsubscribe();
    }

    startCaseLoad(caseId: string) {
        if (this.caseId === caseId) return;

        this.caseId = caseId;
        this.settings = this.parent?.caseId === caseId ? new Map(this.parent.settings) : new Map();
    }

    loadAccountSettings(accountId: string, clientId?: string) {
        if (!this.settings.has(accountId)) {
            const url = new URL(`./AccountSettings/${accountId}`, this.baseApiUrl);
            const settings = defer(() => super.getData<UWResponse<AccountSettingsDTO[]>>(url.toString(), clientId)).pipe(
                map(response => response.data),
                shareReplay({ bufferSize: 1, refCount: false })
            );
            this.settings.set(accountId, settings);
        }

        return this.settings.get(accountId)!;
    }

    getAccountSetting(account: Account, settingName: string) {
        return this.loadAccountSettings(account.id).pipe(
            map(settings => settings.find(setting => setting.name === settingName))
        );
    }
}